import React, { useEffect } from "react";

import { cn } from "@/lib/utils";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { CardType } from "../ProgramCarousel";
import { EnrichedProgram } from "@/common/course/types";
import { ReasonList } from "./components/ReasonList";
import { ProgramCardList } from "./components/ProgramCardList";
import { type Reason } from "./types";

export type ExpandableRelatedProgramsProps = {
  title: string;
  subtitle?: string;
  className?: string;
  reasons?: Reason[];
  cardType?: CardType;
  showMoreLabel?: string;
  showLessLabel?: string;
  programs: EnrichedProgram[];
  showNumberOfPrograms?: boolean;
  backgroundColor?: "putty" | "fern";
};

function ExpandableRelatedPrograms({
  title,
  reasons,
  cardType,
  subtitle,
  className,
  programs = [],
  showMoreLabel,
  showLessLabel,
  backgroundColor = "putty",
  showNumberOfPrograms = true,
}: ExpandableRelatedProgramsProps) {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      name: "related_programs_show_more_viewed",
      location: "course-detail",
      text: "Related Programs Show More",
      htmlId: "related_programs_show_more",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  const colors = {
    putty: "bg-putty-500",
    fern: "bg-secondary-100",
  };

  const pogramString = programs.length > 1 ? "programs" : "program";

  if (programs.length <= 0) {
    return null;
  }

  return (
    <section
      ref={contentRef}
      className={cn(
        "p-4 pb-8 md:p-12 rounded-xl text-gray-800",
        colors[backgroundColor],
        className,
      )}
    >
      <hgroup className="mb-6 text-center">
        <h2 className="font-bold text-primary text-balance text-2xl md:text-3xl m-0">
          {title}
        </h2>
        {subtitle && (
          <p className="text-base text-balance text-inherit mt-3">
            {subtitle}
            {showNumberOfPrograms && (
              <span className="font-bold">
                {programs.length} {pogramString}.
              </span>
            )}
          </p>
        )}
      </hgroup>

      {reasons && <ReasonList reasons={reasons} />}

      <ProgramCardList
        cardType={cardType}
        programs={programs}
        showMoreLabel={showMoreLabel}
        showLessLabel={showLessLabel}
      />
    </section>
  );
}

export default ExpandableRelatedPrograms;
